import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import InfoButton from '../../common/InfoButton'
import { ReactComponent as Checkbox } from '../../../assets/svg/CheckedCheckbox.svg'
import { ReactComponent as GreenCheckbox } from '../../../assets/svg/CheckedCheckboxGreen.svg'
import ExpandButton from '../../common/ExpandButton'
import { Link } from 'react-router-dom'
import { getFilersForCounsellorsAnalytics } from '../../../server/server'
import { LoadingContainer } from '../student/CheckInWidget'
import { translateTargetText } from '../../../utils/util'
import StudentCountLabel from '../StudentCountLabel'

const getMockCounsellorAnalyticsFilters = () => {
    try {
        const savedFilters = localStorage.getItem('counsellorAnalyticsFilter')
        if (savedFilters) {
            return JSON.parse(savedFilters)
        }
    } catch (error) {
        console.error('Error parsing saved counsellor analytics filters:', error)
    }
    return []
}

function CounsellorCardAnalytics({ setSelectedOptions, selectedOptions }) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(getMockCounsellorAnalyticsFilters())
    const showLoading = useMemo(() => loading || !data, [loading, data])

    useEffect(() => {
        getData()
    }, [])

    const getData = async () => {
        const resp = await getFilersForCounsellorsAnalytics()
        if (resp.status === 200) {
            const data = resp.data?.target_filter.map((e) => {
                let data = { ...e }
                const selected =
                    selectedOptions?.all ||
                    selectedOptions?.selected?.some((item) => {
                        if (Array.isArray(item)) {
                            return item.some((subItem) => subItem.code === e.code)
                        } else {
                            return item.code === e.code
                        }
                    })

                data.checked = selected
                if (!selected) {
                    const list = data.list.map((item) => {
                        const selected =
                            selectedOptions?.all ||
                            selectedOptions?.selected?.some((subItem) => {
                                if (Array.isArray(subItem)) {
                                    return subItem.some((subSubItem) => subSubItem.code === item.code)
                                } else {
                                    return subItem.code === item.code
                                }
                            })
                        item.checked = selected
                        return item
                    })
                    data.list = list
                }

                return data
            })

            setData(data)
        }
        setLoading(false)
    }

    const updateData = (data) => {
        setData(data)
        let parsedFilters = []
        const newFilters = [...data]
        let allSelected = true
        for (let filter of newFilters) {
            const list = filter.list
            let parsedSubFilter = []
            if (filter.checked) {
                parsedFilters.push({ code: filter['code'], field: filter['field'] })
            } else {
                allSelected = false
                for (let subFilter of list) {
                    if (subFilter.checked) {
                        console.log(subFilter)
                        parsedSubFilter.push({ code: subFilter['code'], field: subFilter['field'] })
                    } else {
                        for (let subSubFilter of subFilter?.list ?? []) {
                            if (subSubFilter.checked) {
                                parsedSubFilter.push({ code: subSubFilter['code'], field: subSubFilter['field'] })
                            }
                        }
                    }
                }
                if (parsedSubFilter.length > 0) parsedFilters.push(parsedSubFilter)
            }
        }
        if (parsedFilters.length === 0) {
            for (let filter of newFilters) {
                parsedFilters.push({ code: filter['code'], field: filter['field'] })
            }
            allSelected = true
        }

        setSelectedOptions({ selected: parsedFilters, all: allSelected })
        localStorage.setItem('counsellorAnalyticsFilter', JSON.stringify(data))
    }

    return (
        <div className="mt-[10px] pb-8 px-8">
            <div className="relative">
                <CounsellorClassFilters data={data} blur={showLoading} setData={updateData} />
                {showLoading && <LoadingContainer showEmptyData={!loading && !data} />}
            </div>
        </div>
    )
}

export default CounsellorCardAnalytics

export const CounsellorClassFilters = ({ data, blur, setData, infoText = 'Select which classes to include in the school-wide analytics view.' }) => {
    const { t } = useTranslation()

    const isAllSelected = data.every((item) => item.checked)

    const handleSelectAllClasses = () => {
        const newData = data.map((item) => {
            if (item.list) {
                item.list = item.list.map((classItem) => ({ ...classItem, checked: true }))
            }
            return { ...item, checked: true }
        })
        setData(newData)
    }

    return (
        <div className={blur ? 'blur' : ''} data-tour="class_emotional_info">
            <div className="flex flex-row items-center mt-5">
                <div className="flex gap-6 flex-col w-full">
                    <div className="flex flex-row items-center justify-between h-[30px]">
                        <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{t('All classes')}</p>
                        <div className="flex flex-row items-center gap-[15px]">
                            {!isAllSelected && (
                                <button
                                    className=" h-[30px] px-[15px] flex items-center justify-center rounded-full border border-[#bebece] transition bg-[#FAFAFF] hover:bg-lightBG"
                                    onClick={handleSelectAllClasses}
                                >
                                    <p className="text-[14px] leading-[20px] font-semibold text-deepPurple ">{t('Select all classes')}</p>
                                </button>
                            )}
                            <InfoButton color="#77768D" text={t(infoText)} />
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        {data?.map((item, index) => {
                            const updateFilter = (value) => {
                                let newList = [...data]
                                newList[index] = { ...value }
                                setData(newList)
                            }
                            return <SingleYearItem item={item} loadData={() => {}} key={index} updateItem={updateFilter} />
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}

export const SingleYearItem = ({ item, initialOpen = false, updateItem }) => {
    const { t } = useTranslation()
    const haveChildren = !!item.list
    const [open, setOpen] = useState(initialOpen)

    const isYearSelected = item.checked

    const checked = !!item.checked

    const checkFilter = (e) => {
        e.stopPropagation()
        let newItem = { ...item, checked: !checked }
        if (haveChildren) {
            newItem.list = newItem.list.map((e) => ({ ...e, checked: !checked }))
        }
        updateItem(newItem)
    }

    return (
        <div className="bg-[#F8F8FB] w-full rounded-[8px]" data-tour="single_item">
            <div
                className="flex flex-row items-center gap-3 pointer rounded-[8px] px-3 h-10 transition"
                onClick={(event) => haveChildren ? setOpen((prev) => !prev) : checkFilter(event)}
                style={{ backgroundColor: open ? '#ECEBF8' : 'transparent' }}
            >
                <button type="button" role="checkbox" aria-checked={checked} aria-label={translateTargetText(item?.title)} className="h-[18px] w-[24px] flex items-center justify-center" onClick={checkFilter}>
                    {isYearSelected ? <GreenCheckbox height={18} width={18} /> : <Checkbox height={16} width={16} />}
                </button>
                <p className="text-darkenPurple text-[16px] leading-5 font-semibold flex-1 min-w-0 truncate" title={translateTargetText(item?.title)}>{translateTargetText(item?.title)}</p>
                <StudentCountLabel count={item.studentCount} />
                {haveChildren ? <ExpandButton open={open} onClick={() => {}} backgroundColor="transparent" width={24} height={24} /> : <div className="w-[24px]" />}
            </div>
            <div className={`overflow-hidden transition-all px-2 ${open ? 'max-h-[1000px] h-auto' : 'max-h-0'} ${open ? 'opacity-100 py-[10px]' : 'opacity-0'}`}>
                <div className="flex flex-col gap-[10px]">
                    {item?.list?.map((classItem, index) => {
                        const updateFilterItem = (value) => {
                            let newList = [...item.list]
                            newList[index] = { ...value }
                            const newItem = { ...item, list: newList, checked: newList.every((e) => e.checked) }
                            updateItem(newItem)
                        }
                        return <SingleClassItem classItem={classItem} yearItem={item.code} key={classItem.code} updateItem={updateFilterItem} />
                    })}
                </div>
            </div>
        </div>
    )
}

const SingleClassItem = ({ classItem, yearItem, updateItem }) => {
    const { t } = useTranslation()

    const isClassSelected = classItem.checked

    const handleClassSelection = (e) => {
        e.stopPropagation()

        let newItem = { ...classItem, checked: !isClassSelected }

        updateItem(newItem)
    }

    return (
        <div className="flex flex-row items-center gap-3 pl-1 py-2 border-b border-b-[#ECEBF8] min-h-[30px]">
            <button className="h-[18px] w-[24px] flex items-center justify-center " onClick={handleClassSelection}>
                {isClassSelected ? <GreenCheckbox height={18} width={18} /> : <Checkbox height={16} width={16} />}
            </button>
            <div className="flex-1 min-w-0">
                <p className="text-darkenPurple text-[14px] leading-5 font-semibold break-words">{translateTargetText(classItem?.title)}</p>
                <StudentCountLabel count={classItem.studentCount} />
            </div>
            <Link to={`/analytics/class?id=${classItem.code}`} className="text-deepPurple text-[14px] leading-5 font-semibold underline">
                {t('See analytics')}
            </Link>
        </div>
    )
}
